import { useParams, useNavigate } from "react-router-dom";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useDocumentsGroupedByUser } from "../../hooks/useDocumentsGroupedByUser";
import { useUserBankingChangeRequests } from "../../hooks/useUserBankingChangeRequests";
import { DocumentCard } from "../components/document-card";
import { BankingDetailsCard } from "../components/banking-details-card";
import { overallStatusChip, formatRelativeTime } from "../../utils";

export function UserDocumentsReviewPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  // pageSize 100: partners typically have 2-5 documents; avoids pagination complexity on a review screen.
  const { data, isLoading } = useDocumentsGroupedByUser({
    userId,
    page: 1,
    pageSize: 100,
  });

  const userDocuments = data?.data?.[0];
  const documents = userDocuments?.documents ?? [];
  const { bankingRequests } = useUserBankingChangeRequests(userId);

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <ShimmerLoader message="Loading partner documents…" lines={4} />
      </div>
    );
  }

  if (!userDocuments) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <Text variant="body" tone="muted">Partner documents not found.</Text>
      </div>
    );
  }

  const submittedAt = documents[0]?.createdAt;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      {/* Breadcrumb */}
      {/* Phones: just the back link — the name is the page title right below. */}
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(AppRoutes.ADMIN_DOCUMENTS)} className="hover:underline">
          <Heading variant="h3" tone="accent">← Registration Queue</Heading>
        </button>
        <Heading variant="h3" tone="muted" className="max-md:hidden">/</Heading>
        <Heading variant="h3" tone="dark" className="max-md:hidden">{userDocuments.userName}</Heading>
      </div>

      {/* Title row */}
      <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-start max-md:gap-2">
        <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-2">
          <Heading variant="h2" tone="dark">{userDocuments.userName}</Heading>
          {documents.length > 0 && overallStatusChip(documents)}
        </div>
        {submittedAt && (
          <Text variant="bodySm" tone="muted">
            Submitted {formatRelativeTime(submittedAt)}
          </Text>
        )}
      </div>

      {/* Document cards */}
      {documents.length === 0 ? (
        <Text variant="body" tone="muted">No documents uploaded yet.</Text>
      ) : (
        <div className="grid grid-cols-2 gap-4 items-start max-md:grid-cols-1">
          {documents.map((doc) => (
            <DocumentCard key={doc.id} document={doc} />
          ))}
        </div>
      )}

      {/* Banking details section */}
      {bankingRequests.length > 0 && (
        <>
          <Heading variant="h3" tone="dark">Banking Details</Heading>
          <div className="grid grid-cols-2 gap-4 items-start max-md:grid-cols-1">
            {bankingRequests.map((cr) => (
              <BankingDetailsCard key={cr.id} changeRequest={cr} />
            ))}
          </div>
        </>
      )}

      {/* Footer note */}
      <Text variant="bodySm" tone="muted">
        {userDocuments?.partnerType === "seller"
          ? "Approving all documents and banking details will activate this supplier account. The supplier will be notified by email."
          : "Approving both documents will activate this buyer account. The buyer will be notified by email."}
      </Text>
    </div>
  );
}
